import type { TextAreaRef } from "../../../../../shared/frontend/ui";
import { SettingOutlined } from "@ant-design/icons";
import { Button, Switch } from "../../../../../shared/frontend/ui";
import React from "react";
import { spacing } from "../app/design-system";
import { useI18n } from "../app/i18n/useI18n";
import {
  buildInsertedTemplateToken,
  buildTemplateVariableToken,
  findTemplateTokenAtCursor,
  getTemplateVariableFormulaType,
  templateFormulaDefinitions,
  type TemplateTokenAtCursor
} from "../app/templates/templateFormulaOptions";
import type { TemplateVariable } from "../app/templates/templateVariables";
import { TemplateComboBox } from "./TemplateComboBox";

/**
 * Die Schreibfläche, auf der die Werkzeugleiste arbeitet.
 *
 * Ein Textfeld und ein bearbeitbarer Absatz in einer Dokumentvorschau können dasselbe: an der
 * Schreibmarke einfügen und die Variable darunter ersetzen. Womit sie das tun, ist verschieden —
 * deshalb steht es hier als Vertrag und nicht als zweite Werkzeugleiste.
 */
export type TemplateVariableEditorTarget = {
  /** Fügt Text an der Schreibmarke ein. */
  insert: (value: string) => void;
  /** Die Variable an der Schreibmarke, oder <c>null</c>. */
  tokenAtCaret: () => TemplateTokenAtCursor | null;
  /** Ersetzt die Variable an der Schreibmarke durch einen neuen Ausdruck. */
  replaceToken: (token: TemplateTokenAtCursor, next: string) => void;
};

type TemplateVariableToolbarProps = {
  /** Angebotene Variablen; Gruppe und Bezeichnung kommen übersetzt aus dem Katalog. */
  variables: TemplateVariable[];
  /** Schreibfläche. Ohne sie wird aus `value`/`onChange`/`textAreaRef` eine gebaut. */
  target?: TemplateVariableEditorTarget;
  /** Der bearbeitete Text — nur für den Textfeld-Fall. */
  value?: string;
  onChange?: (next: string) => void;
  /** Textfeld, in dem eingefügt wird. Ohne Schreibmarke landet die Variable am Ende. */
  textAreaRef?: React.RefObject<TextAreaRef | null>;
  /** Schalter „Vorschauwerte"; ohne Rückruf wird er nicht angeboten. */
  previewEnabled?: boolean;
  onPreviewEnabledChange?: (enabled: boolean) => void;
  disabled?: boolean;
  /** Zusätzliche Steuerung vor „Variable einfügen", z. B. die gewählte Dokumentausgabe. */
  leading?: React.ReactNode;
  /** Zusätzliche Anzeige rechts, z. B. ein Zeichenzähler. */
  extra?: React.ReactNode;
  /** Öffnet die Konfiguration der Blockvariable an der Schreibmarke. */
  onBlockVariableSelect?: (name: string) => void;
  /** Die Werkzeugleiste liegt bereits in einer Kopfzeile und braucht keinen eigenen unteren Abstand. */
  compact?: boolean;
};

/**
 * Variable einfügen, Formelteil setzen, Vorschauwerte einblenden — die eine Werkzeugleiste für
 * alle Flächen mit Variablen.
 *
 * Sie lag zuvor dreimal nebeneinander: im SMS-Zweig des `CommunicationComposer`, im
 * dem Kommunikationseditor und im Systemdokument-Editor. Jede Kopie konnte
 * etwas anderes, und eine Verbesserung erreichte nur eine davon.
 *
 * Der Formelteil bezieht sich auf die Variable **an der Schreibmarke**. Erfasst wird sie beim
 * Öffnen der Auswahl: Danach liegt der Fokus in der Auswahl, nicht mehr im Text.
 */
export function TemplateVariableToolbar({
  variables,
  target,
  value = "",
  onChange,
  textAreaRef,
  previewEnabled = false,
  onPreviewEnabledChange,
  disabled,
  leading,
  extra,
  onBlockVariableSelect,
  compact = false
}: TemplateVariableToolbarProps): React.ReactElement {
  const { text } = useI18n();
  const [activeToken, setActiveToken] = React.useState<TemplateTokenAtCursor | null>(null);
  const [variableResetKey, setVariableResetKey] = React.useState(0);
  const [formulaResetKey, setFormulaResetKey] = React.useState(0);

  const textArea = React.useCallback((): HTMLTextAreaElement | null => {
    const element = textAreaRef?.current?.nativeElement;
    return element instanceof HTMLTextAreaElement ? element : null;
  }, [textAreaRef]);

  /** Der Textfeld-Fall als Schreibfläche — damit der Rest der Leiste nur einen Weg kennt. */
  const textAreaTarget = React.useMemo<TemplateVariableEditorTarget>(
    () => ({
      insert: (token) => {
        const element = textArea();
        const start = element?.selectionStart;
        const end = element?.selectionEnd;
        const next = typeof start === "number" && typeof end === "number"
          ? value.slice(0, start) + token + value.slice(end)
          : value + token;
        const caret = typeof start === "number" ? start + token.length : next.length;

        onChange?.(next);
        // Nach dem Wert setzen, sonst überschreibt das Neuzeichnen die Schreibmarke wieder.
        setTimeout(() => {
          element?.focus();
          element?.setSelectionRange(caret, caret);
        }, 0);
      },
      tokenAtCaret: () => {
        const element = textArea();
        if (!element || typeof element.selectionStart !== "number") return null;
        return findTemplateTokenAtCursor(value, element.selectionStart);
      },
      replaceToken: (token, replacement) => {
        const next = value.slice(0, token.start) + replacement + value.slice(token.end);
        const caret = token.start + replacement.length;
        onChange?.(next);
        setTimeout(() => {
          const element = textArea();
          element?.focus();
          element?.setSelectionRange(caret, caret);
        }, 0);
      }
    }),
    [onChange, textArea, value]
  );

  const editor = target ?? textAreaTarget;

  const variableOptions = React.useMemo(() => {
    const byGroup = new Map<string, Array<{ value: string; label: string; searchLabel: string }>>();
    for (const variable of variables) {
      const group = text(variable.group);
      const label = text(variable.label);
      if (!byGroup.has(group)) byGroup.set(group, []);
      byGroup.get(group)!.push({
        value: variable.key,
        label,
        searchLabel: `${group} ${label} ${variable.key}`
      });
    }
    return Array.from(byGroup.entries()).map(([label, options]) => ({ label, options }));
  }, [text, variables]);

  const formulaOptions = React.useMemo(() => {
    const valueType = activeToken?.valueType;
    if (valueType !== "date" && valueType !== "number" && valueType !== "link") return [];

    const fixedOptions = templateFormulaDefinitions
      .filter((option) => option.valueType === valueType)
      .map((option) => ({ value: option.value, label: text(option.labelKey) }));
    if (valueType !== "number") return fixedOptions;

    // Zahlenwerte lassen sich addieren — angeboten werden die übrigen Zahlenvariablen.
    const variableOptionsForSum = variables
      .filter((variable) => variable.key !== activeToken?.key)
      .filter((variable) => getTemplateVariableFormulaType(variable.key) === "number")
      .map((variable) => ({ value: `+ ${variable.key}`, label: `+ ${text(variable.label)}` }));

    return [...fixedOptions, ...variableOptionsForSum];
  }, [activeToken?.key, activeToken?.valueType, text, variables]);

  return (
    <div
      className={compact ? "template-variable-toolbar template-variable-toolbar-compact" : "template-variable-toolbar"}
      style={{ display: "flex", alignItems: "center", gap: spacing.xs, flex: "1 1 auto", minWidth: 0, overflow: "hidden" }}
    >
      {leading}

      {variableOptions.length > 0 ? (
        <>
          <TemplateComboBox
            key={`variable-${variableResetKey}`}
            options={variableOptions}
            onSelect={(key) => {
              const variable = variables.find((item) => item.key === key);
              if (!variable) return;
              const linkLabel = variable.link?.displayText ? text(variable.link.displayText) : undefined;
              editor.insert(buildInsertedTemplateToken(variable, linkLabel));
              setVariableResetKey((current) => current + 1);
            }}
            placeholder={text("Insert variable…")}
            ariaLabel={text("Insert variable")}
            disabled={disabled || previewEnabled}
            style={{ minWidth: 140, maxWidth: 260, flex: "1 1 auto" }}
          />
          <TemplateComboBox
            key={`formula-${formulaResetKey}`}
            options={[{ options: formulaOptions }]}
            onOpen={() => setActiveToken(editor.tokenAtCaret())}
            onSelect={(formulaPart) => {
              if (!activeToken || activeToken.valueType === "text") return;
              editor.replaceToken(activeToken, buildTemplateVariableToken(activeToken.key, String(formulaPart)));
              setActiveToken(null);
              setFormulaResetKey((current) => current + 1);
            }}
            placeholder={text("Formula part placeholder")}
            ariaLabel={text("Formula part")}
            disabled={disabled || previewEnabled}
            notFoundContent={text("No formula at cursor")}
            style={{ minWidth: 140, maxWidth: 280, flex: "1 1 auto" }}
          />
        </>
      ) : null}

      {onPreviewEnabledChange && variables.length > 0 ? (
        <label className="communication-composer-preview-toggle">
          <Switch checked={previewEnabled} onChange={onPreviewEnabledChange} />
          <span>{text("Preview values")}</span>
        </label>
      ) : null}

      {onBlockVariableSelect ? (
        <Button
          icon={<SettingOutlined />}
          disabled={disabled || previewEnabled}
          onClick={() => {
            const token = editor.tokenAtCaret();
            if (token?.key.startsWith("block.")) return onBlockVariableSelect(token.key);
          }}
        >
          {text("Format block")}
        </Button>
      ) : null}

      {extra}
    </div>
  );
}
